// Share your plan (redesign slice 8b; wireframe 12;
// docs/2026-10-04-sharing-design.md, section 2). One sheet for the whole
// week or one day, from the week menu and the day view, replacing the week,
// day and publish panels:
//
//   Send or post it    a preview, "Share to an app", "Copy link", the
//                      week's or day's one link on or off, and for a week
//                      "Include snacks, preserves and medicinal". A plan
//                      link shows recipe names; a recipe opens from it only
//                      when it has its own link on (0060), and the sheet
//                      says how many of yours do.
//   Plan together      share with another household by email; they edit
//                      it with you under "Shared with us". Remove.
//   Publish            the whole week as a reusable, dateless plan (its
//                      recipes must be public first to publish publicly).
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useMealPlanner } from "../../hooks/MealPlannerContext";
import {
  createMealPlanTemplate,
  createWeekPlanDayShareLink,
  createWeekPlanShareLink,
  listWeekPlanDayShareLinks,
  listWeekPlanDayShares,
  listWeekPlanShareLinks,
  listWeekPlanShares,
  loadMyRecipeLinkIds,
  revokeWeekPlanDayShare,
  revokeWeekPlanDayShareLink,
  revokeWeekPlanShare,
  revokeWeekPlanShareLink,
  shareWeekPlan,
  shareWeekPlanDay,
  weekDataToTemplateSnapshot,
} from "../../lib/db";
import { COLORS, FONT_SIZE, LAYOUT, RADIUS, SPACE } from "../../lib/constants";
import { orderedDays } from "../../lib/utils";
import { mealDate } from "../../lib/planScaling";
import { planRecipeIds, weekRange, weekSummary } from "../../lib/planView";
import type { Day } from "../../lib/types";
import { Sheet } from "../Sheet";
import { Toggle } from "../Toggle";
import { smallLabel, supporting } from "../recipeStyles";
import { inputStyle, textButton } from "./planStyles";
import { primaryButton, secondaryButton } from "../recipes/writeStyles";
import { Chip } from "../recipes/LibraryFilters";

interface LinkRow {
  id: string;
  token: string;
  include_snacks?: boolean;
}
interface HouseholdShare {
  id: string;
  shared_with_emails?: string[];
}

const ALL_LISTS = { includeSnacks: true, includePreserves: true, includeMedicinal: true };
const NO_LISTS = { includeSnacks: false, includePreserves: false, includeMedicinal: false };

export function PlanShareSheet({ initialDay, openPublish = false, onClose }: { initialDay: Day | null; openPublish?: boolean; onClose: () => void }) {
  const mp = useMealPlanner();
  const days = orderedDays(mp.planDefaults.weekStartDay) as Day[];
  const [day, setDay] = useState<Day | null>(initialDay);
  const [link, setLink] = useState<LinkRow | null>(null);
  const [shares, setShares] = useState<HouseholdShare[]>([]);
  const [myLinked, setMyLinked] = useState<Set<string>>(new Set());
  const [includeLists, setIncludeLists] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const { householdId, weekStart, weekData } = mp;

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError("");
    const linkRows = day ? listWeekPlanDayShareLinks(householdId, weekStart, day) : listWeekPlanShareLinks(householdId, weekStart);
    const shareRows = day ? listWeekPlanDayShares(householdId, weekStart, day) : listWeekPlanShares(householdId, weekStart);
    Promise.all([linkRows, shareRows, loadMyRecipeLinkIds().catch(() => new Set<string>())])
      .then(([links, rows, linked]) => {
        if (cancelled) return;
        const first = ((links as LinkRow[]) || [])[0] ?? null;
        setLink(first);
        if (first && !day) setIncludeLists(!!first.include_snacks);
        setShares((rows as HouseholdShare[]) || []);
        setMyLinked(linked as Set<string>);
      })
      .catch(() => !cancelled && setError("Couldn't load how this plan is shared."))
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [householdId, weekStart, day]);

  const prefix = day ? "/shared-day" : "/shared-plan";
  const url = (token: string) => `${window.location.origin}${prefix}/${token}`;
  const label = day ? mealDate(weekStart, day).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" }) : `Week of ${weekRange(weekStart)}`;
  const summary = weekSummary(day ? { ...weekData, days: { [day]: weekData.days[day] } } : weekData, day ? [day] : days);

  // Which recipes open from the link: your own that have their link on.
  const ids = planRecipeIds(weekData, day, !day && includeLists);
  const mine = [...ids].filter((id) => mp.recipeById(id)?.ownerUserId === mp.userId);
  const opening = mine.filter((id) => myLinked.has(id)).length;
  const namesOnly = ids.size - opening;

  async function turnOn(lists = includeLists): Promise<LinkRow | null> {
    if (link) return link;
    setBusy(true);
    setError("");
    try {
      const made = (day ? await createWeekPlanDayShareLink(householdId, weekStart, day) : await createWeekPlanShareLink(householdId, weekStart, lists ? ALL_LISTS : NO_LISTS)) as LinkRow;
      setLink(made);
      return made;
    } catch (e) {
      setError((e as Error).message || "Couldn't turn the link on.");
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function turnOff() {
    if (!link) return;
    setBusy(true);
    setError("");
    try {
      await (day ? revokeWeekPlanDayShareLink(link.id) : revokeWeekPlanShareLink(link.id));
      setLink(null);
    } catch (e) {
      setError((e as Error).message || "Couldn't turn the link off.");
    } finally {
      setBusy(false);
    }
  }

  // A link's lists are fixed when it is made, so changing them while it is
  // on makes a new link (the old one stops working).
  async function setLists(on: boolean) {
    setIncludeLists(on);
    if (!link || day) return;
    setBusy(true);
    try {
      await revokeWeekPlanShareLink(link.id);
      const made = (await createWeekPlanShareLink(householdId, weekStart, on ? ALL_LISTS : NO_LISTS)) as LinkRow;
      setLink(made);
    } catch (e) {
      setError((e as Error).message || "Couldn't change the link.");
    } finally {
      setBusy(false);
    }
  }

  async function shareToApp() {
    const l = await turnOn();
    if (!l) return;
    try {
      await navigator.share({ title: label, text: `${label} · ${summary}`, url: url(l.token) });
    } catch {
      // Closing the share menu without choosing is not an error.
    }
  }

  async function copyLink() {
    const l = await turnOn();
    if (!l) return;
    try {
      await navigator.clipboard.writeText(url(l.token));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Couldn't copy. The link is shown below to copy by hand.");
    }
  }

  async function shareWith(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      if (day) {
        await shareWeekPlanDay(householdId, weekStart, day, email.trim());
        setShares((await listWeekPlanDayShares(householdId, weekStart, day)) as HouseholdShare[]);
      } else {
        await shareWeekPlan(householdId, weekStart, email.trim(), includeLists ? ALL_LISTS : NO_LISTS);
        setShares((await listWeekPlanShares(householdId, weekStart)) as HouseholdShare[]);
      }
      setEmail("");
    } catch (err) {
      setError((err as Error).message || "Couldn't share the plan.");
    } finally {
      setBusy(false);
    }
  }

  async function removeShare(id: string) {
    setBusy(true);
    setError("");
    try {
      await (day ? revokeWeekPlanDayShare(id) : revokeWeekPlanShare(id));
      setShares((s) => s.filter((x) => x.id !== id));
    } catch (err) {
      setError((err as Error).message || "Couldn't remove that share.");
    } finally {
      setBusy(false);
    }
  }

  const canShareToApp = typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <Sheet title="Share your plan" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: SPACE.x3 }}>
        <div role="group" aria-label="What to share" style={{ display: "flex", flexWrap: "wrap", gap: SPACE.sm, alignItems: "center" }}>
          <Chip on={!day} onClick={() => setDay(null)}>
            The whole week
          </Chip>
          <span style={{ display: "inline-flex", alignItems: "center", gap: SPACE.sm }}>
            <Chip on={!!day} onClick={() => setDay(day ?? days[0])}>
              One day
            </Chip>
            <select aria-label="Which day" value={day ?? ""} onChange={(e) => setDay((e.target.value as Day) || null)} style={{ ...inputStyle, minHeight: 36 }}>
              <option value="">Pick a day</option>
              {days.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </span>
        </div>

        {error && <p style={{ color: COLORS.danger, margin: 0 }}>{error}</p>}
        {!loaded ? (
          <p style={supporting}>Loading…</p>
        ) : (
          <>
            <Part title="Send or post it">
              <div style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.xl, padding: "var(--space-lg) var(--space-xl)", background: COLORS.card }}>
                <div style={{ fontFamily: "var(--font-serif)", fontSize: FONT_SIZE.h4, fontWeight: 600 }}>{weekData.title && !day ? `${weekData.title}: ${weekRange(weekStart)}` : label}</div>
                <div style={supporting}>
                  {summary} · {window.location.host}
                </div>
              </div>
              <p style={{ ...supporting, margin: 0 }}>This is the preview people see in a message or post.</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: SPACE.md }}>
                {canShareToApp && (
                  <button type="button" onClick={shareToApp} disabled={busy} style={primaryButton}>
                    Share to an app
                  </button>
                )}
                <button type="button" onClick={copyLink} disabled={busy} style={canShareToApp ? secondaryButton : primaryButton}>
                  {copied ? "Copied" : "Copy link"}
                </button>
              </div>
              {!day && (
                <label style={{ display: "flex", alignItems: "center", gap: SPACE.md, fontSize: FONT_SIZE.base }}>
                  <input type="checkbox" className="mp-check" checked={includeLists} disabled={busy} onChange={(e) => setLists(e.target.checked)} />
                  Include snacks, preserves and medicinal
                </label>
              )}
              <Toggle on={!!link} disabled={busy} label={link ? "Link is on" : "Link is off"} onChange={(on) => (on ? turnOn() : turnOff())}>
                {link ? "Anyone with the link can view the plan. View only, no account needed. Turn it off to stop every copy of the link." : "Turn it on to send or post the plan. Anyone with the link can view it; no account needed."}
              </Toggle>
              {link && <input readOnly aria-label="The plan's link" value={url(link.token)} onFocus={(e) => e.target.select()} style={{ ...inputStyle, width: "100%", fontSize: FONT_SIZE.sm }} />}
              {ids.size > 0 && (
                <p style={{ ...supporting, margin: 0 }}>
                  People with the link see recipe names. {opening > 0 ? `${opening} of your recipes here open from it, because their own link is on; ` : ""}
                  {namesOnly > 0 ? `${namesOnly} ${namesOnly === 1 ? "shows" : "show"} by name only.` : ""} Turn a recipe&apos;s link on from its Share to let people open it.
                </p>
              )}
            </Part>

            <Part title="Plan together with another household">
              <p style={{ ...supporting, margin: 0 }}>They see it under &ldquo;Shared with us&rdquo; and can edit it with you as you go.</p>
              <form onSubmit={shareWith} style={{ display: "flex", gap: SPACE.md, flexWrap: "wrap" }}>
                <input type="email" aria-label="Their email" placeholder="Their email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ ...inputStyle, flex: "1 1 200px", minWidth: 0 }} />
                <button type="submit" disabled={!email.trim() || busy} style={{ ...primaryButton, opacity: email.trim() ? 1 : 0.6 }}>
                  Share
                </button>
              </form>
              {shares.map((s) => (
                <div key={s.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.md, borderBottom: `1px solid ${COLORS.line}`, minHeight: LAYOUT.tapTarget }}>
                  <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                    {(s.shared_with_emails || []).join(", ") || "A household"}
                    <span style={supporting}> · {day ?? "Whole week"} · can edit</span>
                  </span>
                  <button type="button" onClick={() => removeShare(s.id)} disabled={busy} style={{ ...textButton, color: COLORS.danger, fontSize: FONT_SIZE.sm }}>
                    Remove
                  </button>
                </div>
              ))}
              <span style={supporting}>Your recipes in this plan are readable by them while the plan is shared.</span>
            </Part>

            {!day && <PublishPart startOpen={openPublish} />}
          </>
        )}
      </div>
    </Sheet>
  );
}

// "Publish as a reusable plan": a dateless copy of the week. Publishing it
// for everyone needs its recipes to be public first (0026 checks this too).
function PublishPart({ startOpen }: { startOpen: boolean }) {
  const mp = useMealPlanner();
  const [open, setOpen] = useState(startOpen);
  const [title, setTitle] = useState(mp.weekData.title || "");
  const [isPublic, setIsPublic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const ids = planRecipeIds(mp.weekData, null, true);
  const privateOnes = mp.recipes.filter((r) => ids.has(r.id) && !r.isPublic);

  async function publish(e: FormEvent) {
    e.preventDefault();
    if (!title.trim() || busy || (isPublic && privateOnes.length > 0)) return;
    setBusy(true);
    setError("");
    try {
      await createMealPlanTemplate(mp.householdId, weekDataToTemplateSnapshot(mp.weekData), title.trim(), isPublic);
      setDone(true);
    } catch (err) {
      setError((err as Error).message || "Couldn't publish this plan.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Part title="Publish as a reusable plan">
      <p style={{ ...supporting, margin: 0 }}>A dateless copy that you, or anyone on The Weekly Kitchen if you make it public, can use as a starting point. Whole week only; later changes to the week don&apos;t change it.</p>
      {done ? (
        <p style={{ margin: 0, color: COLORS.accent, fontWeight: 700 }}>Published. It&apos;s under Plan › Public plans.</p>
      ) : !open ? (
        <button type="button" onClick={() => setOpen(true)} style={{ ...secondaryButton, alignSelf: "flex-start" }}>
          Review and publish
        </button>
      ) : (
        <form onSubmit={publish} style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
          <input aria-label="Name for the plan" placeholder="A name, like “Weeknight basics”" value={title} onChange={(e) => setTitle(e.target.value)} style={{ ...inputStyle, width: "100%" }} />
          <label style={{ display: "flex", alignItems: "center", gap: SPACE.md, fontSize: FONT_SIZE.base }}>
            <input type="checkbox" className="mp-check" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
            Public: anyone on The Weekly Kitchen can browse and use it
          </label>
          {privateOnes.length > 0 && (
            <p style={{ ...supporting, margin: 0, color: isPublic ? COLORS.danger : COLORS.soft }}>
              {privateOnes.length} recipe{privateOnes.length === 1 ? " in this week is" : "s in this week are"} private: {privateOnes.map((r) => r.title).join(", ")}. Make {privateOnes.length === 1 ? "it" : "them"} public first, or publish the plan for your household only.
            </p>
          )}
          {error && <p style={{ color: COLORS.danger, margin: 0 }}>{error}</p>}
          <button type="submit" disabled={!title.trim() || busy || (isPublic && privateOnes.length > 0)} style={{ ...primaryButton, alignSelf: "flex-start", opacity: !title.trim() || (isPublic && privateOnes.length > 0) ? 0.6 : 1 }}>
            {busy ? "Publishing…" : "Publish"}
          </button>
        </form>
      )}
    </Part>
  );
}

function Part({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
      <h3 style={{ ...smallLabel, color: COLORS.soft, margin: 0 }}>{title}</h3>
      {children}
    </section>
  );
}
